import { zodResolver } from "@hookform/resolvers/zod";
import { Button, MenuItem, TextField } from "@mui/material";
import { BedIcon, PlusCircleIcon, TrashIcon } from "@phosphor-icons/react";
import { times } from "lodash-es";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useTranslations } from "use-intl";
import { object } from "zod";

import { useFeatureIsOn } from "@/app/featureFlags";
import { Card } from "@/components/Card/Card";
import { CounterButton } from "@/components/CounterButton/CounterButton";
import { IconButton } from "@/components/IconButton/IconButton";
import { TravellerTypeSelectionItem } from "@/components/TravellerTypeSelectionItem/TravellerTypeSelectionItem";
import type { TRoom } from "@/features/hotels/types/types";
import {
  MAX_CHILDREN_PER_ROOM,
  MAX_VISITORS_PER_ROOM,
} from "@/features/hotels/utils/constants";
import {
  getRoomsValidationSchema,
  type HotelSearchFormValues,
} from "@/features/hotels/utils/hotelsValidation";
import { MIN_ADULT_AGE } from "@/utils/constants";

const newRoom: TRoom = {
  adults: 1,
  children: 0,
  childrenAges: [],
};

type Props = {
  value: HotelSearchFormValues["rooms"];
  onConfirm: (values: HotelSearchFormValues["rooms"]) => void;
};

export function VisitorsSelection({ value, onConfirm }: Props) {
  const t = useTranslations();

  const methods = useForm<Pick<HotelSearchFormValues, "rooms">>({
    defaultValues: {
      rooms: value,
    },
    resolver: zodResolver(
      object({
        rooms: getRoomsValidationSchema(t),
      }),
    ),
  });

  const { handleSubmit, control } = methods;

  const {
    fields: rooms,
    remove,
    update,
    append,
  } = useFieldArray({
    control,
    name: "rooms",
  });

  const isMultiRoomBookingEnabled = useFeatureIsOn("multi-room-booking");

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={(event) => {
          event.stopPropagation();

          handleSubmit((data) => {
            onConfirm(data.rooms);
          })(event);
        }}
        className="flex h-full w-full flex-col"
      >
        <div className="mb-3 text-sm text-text-primary">
          {t("hotels.search.guestsPerRoomInformation", {
            maxGuestsPerRoom: MAX_VISITORS_PER_ROOM,
          })}
        </div>

        <div className="space-y-3">
          {rooms.map((room, roomIndex) => {
            const maxChildCount = Math.min(
              MAX_VISITORS_PER_ROOM - (room.adults ?? 1),
              MAX_CHILDREN_PER_ROOM,
            );
            const currentChildCount = room.childrenAges?.length ?? 0;
            const maxAdultCount = MAX_VISITORS_PER_ROOM - currentChildCount;

            return (
              <Card
                key={room.id}
                icon={
                  <BedIcon
                    size={24}
                    color="var(--color-surface-pressed-accent-strong)"
                  />
                }
                title={
                  isMultiRoomBookingEnabled
                    ? `${t("hotels.search.room")} ${roomIndex + 1}`
                    : t("hotels.search.room")
                }
                action={
                  rooms.length > 1 ? (
                    <IconButton
                      aria-label={t("remove")}
                      color="error"
                      onClick={() => remove(roomIndex)}
                      disablePadding
                    >
                      <TrashIcon size={24} />
                    </IconButton>
                  ) : null
                }
              >
                <div className="-my-3 divide-y divide-stroke-neutral-soft *:py-3">
                  <TravellerTypeSelectionItem
                    label={t("hotels.search.adult")}
                    description={t("hotels.search.twelveYearOldPlus")}
                  >
                    <CounterButton
                      value={room.adults ?? 1}
                      onChange={(value) =>
                        update(roomIndex, {
                          ...room,
                          adults: value,
                        })
                      }
                      minValue={1}
                      maxValue={maxAdultCount}
                      minusButtonAriaLabel={t("hotels.search.removeAdult")}
                      plusButtonAriaLabel={t("hotels.search.addAdult")}
                    />
                  </TravellerTypeSelectionItem>

                  <div>
                    <TravellerTypeSelectionItem
                      label={t("hotels.search.child")}
                      description={t("hotels.search.underTwelveYearOld")}
                    >
                      <CounterButton
                        value={currentChildCount}
                        onChange={(value) => {
                          const nextAges = Array.from(
                            { length: value },
                            (_, i) => room.childrenAges?.[i],
                          );
                          update(roomIndex, {
                            ...room,
                            children: value,
                            childrenAges: nextAges,
                          });
                        }}
                        maxValue={maxChildCount}
                        minusButtonAriaLabel={t("hotels.search.removeChild")}
                        plusButtonAriaLabel={t("hotels.search.addChild")}
                      />
                    </TravellerTypeSelectionItem>

                    {/* Children ages */}
                    <div className="grid grid-cols-2 gap-2 not-empty:mt-4">
                      {times(currentChildCount, (childIndex) => {
                        return (
                          <Controller
                            key={childIndex}
                            control={control}
                            name={`rooms.${roomIndex}.childrenAges.${childIndex}`}
                            render={({ field, fieldState }) => (
                              // Select component does not support helperText and requires wrapping with FormControl
                              // and FormHelperText to achieve that, which is a lot more code for the same effect.
                              <TextField
                                select
                                label={t("hotels.search.childNAge", {
                                  number: childIndex + 1,
                                })}
                                type="text"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                value={field.value ?? ""}
                                onChange={(e) => {
                                  field.onChange(Number(e.target.value));
                                }}
                              >
                                {times(MIN_ADULT_AGE, (i) => (
                                  <MenuItem key={i} value={i}>
                                    {i === 0
                                      ? t("hotels.search.underOneYearOld")
                                      : i}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {isMultiRoomBookingEnabled ? (
          <Button
            type="button"
            variant="text"
            startIcon={<PlusCircleIcon size={24} />}
            sx={{ my: "calc(var(--spacing)*3)", alignSelf: "center" }}
            onClick={(e) => {
              append(newRoom);
              e.currentTarget.blur();
            }}
          >
            {t("hotels.search.addRoom")}
          </Button>
        ) : null}
        <Button
          fullWidth
          type="submit"
          variant="contained"
          sx={{
            marginTop: "auto",
          }}
        >
          {t("confirm")}
        </Button>
      </form>
    </FormProvider>
  );
}
